/** @jsxImportSource @emotion/react */
import React, { useState } from 'react'
import { css } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'
import type { DivisionItem } from '../../hooks/useSchedulerDivisions'
import type {
  SchedulerConstraint,
  CreateConstraintData,
} from '../../hooks/useSchedulerConstraints'
import type { Venue } from '../../hooks/useSchedulerLocations'
import type { TimeSlot } from '../../hooks/useSchedulerTimeSlots'
import type { GeneratedGame } from '../GeneratedGamesSection'
import DivisionSidebar from '../DivisionSidebar'
import DivisionDetail from './DivisionDetail'

interface DivisionManagementLayoutProps {
  divisions: DivisionItem[]
  divisionsByConference: Map<string, DivisionItem[]>
  readyPercentage: number
  readyCount: number
  loading?: boolean
  constraints?: SchedulerConstraint[]
  games?: GeneratedGame[]
  timeSlots?: TimeSlot[]
  competition?: { starts_at?: string; ends_at?: string } | null
  onToggleReady: (divisionId: string) => void
  onBulkSetReady?: (divisionIds: string[], isReady: boolean) => Promise<number>
  onToggleConstraintEnabled?: (constraintId: string, enabled: boolean) => void
  onUpdateConstraintType?: (constraintId: string, isHard: boolean) => void
  onDeleteTeam?: (divisionId: string, teamId: string, teamName: string) => void
  onAddConstraint?: (data: CreateConstraintData) => Promise<unknown>
  onDeleteConstraint?: (constraintId: string) => Promise<void>
  venues?: Venue[]
  divisionPriorityOrder?: string[]
  onDivisionPriorityChange?: (newOrder: string[]) => void
}

const DivisionManagementLayout: React.FC<DivisionManagementLayoutProps> = ({
  divisions,
  divisionsByConference,
  readyPercentage,
  readyCount,
  loading,
  constraints = [],
  games = [],
  timeSlots = [],
  competition = null,
  onToggleReady,
  onBulkSetReady,
  onToggleConstraintEnabled,
  onUpdateConstraintType,
  onDeleteTeam,
  onAddConstraint,
  onDeleteConstraint,
  venues,
  divisionPriorityOrder,
  onDivisionPriorityChange,
}) => {
  const [selectedDivisionId, setSelectedDivisionId] = useState<string | null>(
    () => (divisions.length > 0 ? divisions[0].id : null)
  )

  const selectedDivision = divisions.find((d) => d.id === selectedDivisionId)

  return (
    <div css={masterDetailContainer}>
      <div css={sidebarPanel}>
        <DivisionSidebar
          divisions={divisions}
          divisionsByConference={divisionsByConference}
          readyPercentage={readyPercentage}
          readyCount={readyCount}
          totalCount={divisions.length}
          selectedDivisionId={selectedDivisionId}
          onSelectDivision={setSelectedDivisionId}
          onBulkSetReady={onBulkSetReady}
          loading={loading}
          divisionPriorityOrder={divisionPriorityOrder}
          onDivisionPriorityChange={onDivisionPriorityChange}
        />
      </div>
      <div css={detailPanel}>
        {selectedDivision ? (
          <DivisionDetail
            division={selectedDivision}
            divisions={divisions}
            constraints={constraints}
            games={games}
            timeSlots={timeSlots}
            competition={competition}
            onToggleReady={onToggleReady}
            onToggleConstraintEnabled={onToggleConstraintEnabled}
            onUpdateConstraintType={onUpdateConstraintType}
            onDeleteTeam={onDeleteTeam}
            onAddConstraint={onAddConstraint}
            onDeleteConstraint={onDeleteConstraint}
            venues={venues}
          />
        ) : (
          <div css={emptyDetailState}>
            <i className='fas fa-layer-group' css={emptyIcon} />
            <div css={emptyTitle}>Select a Division</div>
            <div css={emptySubtitle}>
              Choose a division from the sidebar to view details
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// --- Styles ---

const masterDetailContainer = css`
  display: flex;
  min-height: calc(100vh - 200px);
`

const sidebarPanel = css`
  width: 320px;
  min-width: 320px;
  position: sticky;
  top: 0;
  height: calc(100vh - 200px);
  overflow-y: auto;
  border-right: 1px solid ${colors.SECONDARY_200};
`

const detailPanel = css`
  flex: 1;
  padding-left: 24px;
  padding-right: 8px;
`

const emptyDetailState = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${colors.NEUTRAL_100};
`

const emptyIcon = css`
  font-size: 40px;
  color: ${colors.NEUTRAL_200};
  margin-bottom: 16px;
`

const emptyTitle = css`
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: ${colors.WHITE};
  margin-bottom: 4px;
`

const emptySubtitle = css`
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: ${colors.NEUTRAL_100};
`

export default DivisionManagementLayout
